import { useState } from "react";
import {
  useListEmailLogs,
  useAiDraftFollowUp,
} from "@workspace/api-client-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Mail, Clock, AlertCircle, ChevronDown, ChevronRight, Reply } from "lucide-react";
import { Link, useLocation } from "wouter";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { apiErrorMessage, apiErrorStatus } from "@/lib/apiError";
import { useAiRateLimitUpdater } from "@/lib/aiRateLimit";
import { AiRateLimitInline } from "@/components/AiRateLimitInline";

const FOLLOWUP_STORAGE_KEY = "mailcraft:compose-followup";

export default function Logs() {
  const [, setLocation] = useLocation();
  const [status, setStatus] = useState<"all" | "sent" | "failed">("all");
  const [expandedProviderErrors, setExpandedProviderErrors] = useState<Set<number>>(new Set());
  const [pendingFollowUpId, setPendingFollowUpId] = useState<number | null>(
    null,
  );

  const { data: logs, isLoading } = useListEmailLogs({
    status: status,
    limit: 100
  });

  const toggleProviderError = (logId: number) => {
    setExpandedProviderErrors((prev) => {
      const next = new Set(prev);
      if (next.has(logId)) {
        next.delete(logId);
      } else {
        next.add(logId);
      }
      return next;
    });
  };

  const followUpMutation = useAiDraftFollowUp();
  const aiRateLimit = useAiRateLimitUpdater();

  const handleDraftFollowUp = async (logId: number) => {
    setPendingFollowUpId(logId);
    try {
      const res = await followUpMutation.mutateAsync({
        data: { emailLogId: logId },
      });
      aiRateLimit.updateFromResponse(res);
      window.sessionStorage.setItem(
        FOLLOWUP_STORAGE_KEY,
        JSON.stringify({
          to: res.to,
          subject: res.subject,
          bodyHtml: res.bodyHtml,
        }),
      );
      toast.success("Follow-up drafted — opening Compose");
      setLocation("/app/compose?followUp=1");
    } catch (e) {
      const status = apiErrorStatus(e);
      if (status === 429) {
        aiRateLimit.updateFromError(e);
      }
      toast.error(
        status === 429
          ? `AI rate limit reached: ${apiErrorMessage(e, "try again later")}`
          : apiErrorMessage(e, "Follow-up draft failed"),
      );
    } finally {
      setPendingFollowUpId(null);
    }
  };

  return (
    <div className="flex-1 overflow-auto p-8 bg-background">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Delivery Logs</h1>
            <p className="text-muted-foreground">History of emails sent through MailCraft.</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <AiRateLimitInline />
            <div className="w-[180px]">
              <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="sent">Sent Successfully</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : logs?.length === 0 ? (
          <div className="text-center py-20 px-4 border-2 border-dashed border-border rounded-xl bg-card">
            <Mail className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-medium mb-2">No logs found</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              {status === "all" ? "You haven't sent any emails yet." : `No emails matching status: ${status}.`}
            </p>
          </div>
        ) : (
          <Card className="overflow-hidden">
            <div className="divide-y divide-border">
              {logs?.map((log) => {
                return (
                  <div
                    key={log.id}
                    className="flex items-stretch hover:bg-muted/50 transition-colors"
                  >
                    <Link
                      href={`/app/logs/${log.id}`}
                      className="block p-4 flex-1 min-w-0"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            {log.status === "sent" ? (
                              <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">Sent</Badge>
                            ) : log.status === "queued" ? (
                              <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/20">Queued</Badge>
                            ) : (
                              <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/20">Failed</Badge>
                            )}
                            {/* "Test" badge surfaces self-addressed
                                preview sends triggered by the Compose
                                page's "Send test" button so they're
                                visually distinguishable from real
                                outbound mail in the log. */}
                            {log.kind === "test" && (
                              <Badge
                                variant="outline"
                                className="bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30"
                                data-testid={`log-row-kind-test-${log.id}`}
                                title="This was a self-addressed test send from Compose."
                              >
                                Test
                              </Badge>
                            )}
                            <h4 className="font-semibold text-foreground truncate">{log.subject}</h4>
                          </div>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground mt-2">
                            <span className="truncate">To: <span className="text-foreground font-medium">{log.to}</span></span>
                            {log.templateName && (
                              <span className="flex items-center gap-1">
                                <span className="w-1 h-1 rounded-full bg-border" />
                                Template: {log.templateName}
                              </span>
                            )}
                            <span
                              className="flex items-center gap-1 min-w-0"
                              data-testid={`log-row-from-${log.id}`}
                            >
                              <span className="w-1 h-1 rounded-full bg-border shrink-0" />
                              <span className="shrink-0">From:</span>
                              {log.fromName && (
                                <span
                                  className="text-foreground font-medium truncate"
                                  data-testid={`log-row-from-name-${log.id}`}
                                >
                                  {log.fromName}
                                </span>
                              )}
                              <span className="font-mono truncate">
                                {log.fromName ? `<${log.fromEmail}>` : log.fromEmail}
                              </span>
                            </span>
                          </div>
                        </div>
                        {log.status === "failed" && log.errorMessage && (
                          <div className="mt-2 text-xs text-destructive bg-destructive/5 p-2 rounded">
                            <div className="flex items-start gap-1.5">
                              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                              <span className="break-all line-clamp-2">{log.errorMessage}</span>
                            </div>
                            {log.providerErrorMessage && (
                              <div className="mt-1.5 pl-5">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    toggleProviderError(log.id);
                                  }}
                                  className="flex items-center gap-1 text-[11px] font-medium text-destructive/80 hover:text-destructive"
                                  data-testid={`button-toggle-provider-details-${log.id}`}
                                >
                                  {expandedProviderErrors.has(log.id) ? (
                                    <ChevronDown className="w-3 h-3" />
                                  ) : (
                                    <ChevronRight className="w-3 h-3" />
                                  )}
                                  {expandedProviderErrors.has(log.id)
                                    ? "Hide provider details"
                                    : "Show provider details"}
                                </button>
                                {expandedProviderErrors.has(log.id) && (
                                  <pre
                                    className="mt-1.5 text-[11px] text-destructive/90 break-words whitespace-pre-wrap font-mono bg-destructive/5 border border-destructive/10 rounded p-2"
                                    data-testid={`text-provider-error-message-${log.id}`}
                                  >
                                    {log.providerErrorMessage}
                                  </pre>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        <div className="flex items-center text-xs text-muted-foreground whitespace-nowrap shrink-0">
                          <Clock className="w-3.5 h-3.5 mr-1.5" />
                          {formatDistanceToNow(new Date(log.sentAt), { addSuffix: true })}
                        </div>
                      </div>
                    </Link>
                    {log.status === "sent" && (
                      <div className="flex items-center pr-4 shrink-0">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs"
                          onClick={() => handleDraftFollowUp(log.id)}
                          disabled={pendingFollowUpId === log.id}
                          data-testid={`button-draft-followup-${log.id}`}
                        >
                          {pendingFollowUpId === log.id ? (
                            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                          ) : (
                            <Reply className="w-3.5 h-3.5 mr-1.5" />
                          )}
                          Draft follow-up
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
